import React, { useLayoutEffect } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './queryClient';
import { BrowserRouter as Router } from 'react-router-dom';
import { ThemeProvider, createTheme, responsiveFontSizes } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import { AuthProvider } from './context/AuthContext';
import { TenantProvider } from './context/TenantContext';
import { ProjectProvider } from './context/ProjectContext';
import { PopupProvider } from './context/PopupContext';
import { TestCycleExecutionProvider } from './context/TestCycleExecutionContext';
import { CollectionExecutionProvider } from './context/CollectionExecutionContext';
import MainDomainRoutes from './components/MainDomainRoutes';
import TenantDomainRoutes from './components/TenantDomainRoutes';
import ResizeObserverErrorBoundary from './components/ResizeObserverErrorBoundary';
import 'prismjs/themes/prism.css';

/** Popper modifier: keep dropdown popper width equal to the anchor (Autocomplete, etc.). */
const popperSameWidthModifier = {
  name: 'sameWidth',
  enabled: true,
  phase: 'beforeWrite' as const,
  requires: ['computeStyles'],
  fn: ({ state }: { state: any }) => {
    state.styles.popper = {
      ...state.styles.popper,
      width: `${state.rects.reference.width}px`,
    };
  },
  effect: ({ state }: { state: any }) => {
    const reference = state.elements.reference;
    const popper = state.elements.popper;
    if (reference instanceof HTMLElement && popper instanceof HTMLElement) {
      popper.style.width = `${reference.offsetWidth}px`;
    }
  },
};

/** Base UI scale: 16px at 100% zoom for readable body text on 1080p laptops (was 0.95rem ~15.2px). */
const APP_BASE_FONT_SIZE = '1rem';
const APP_CAPTION_FONT_SIZE = '0.875rem';
const SIDEBAR_TEXT_FONT_SIZE = APP_BASE_FONT_SIZE;
const SIDEBAR_TEXT_FONT_WEIGHT = 500;
const SIDEBAR_TEXT_FONT_FAMILY = '"Inter", "Roboto", "Helvetica", "Arial", sans-serif';

let theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#326bda',
      dark: '#2c60c9',
      light: '#5279e8',
    },
    secondary: {
      main: '#0E185F',
      light: '#5C6BC0',
      dark: '#080f45',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f3f6fb',
      paper: '#ffffff',
    },
    text: {
      primary: '#172441',
      secondary: '#64748d',
    },
    divider: '#e7edf5',
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: SIDEBAR_TEXT_FONT_FAMILY,
    fontSize: 16,
    h1: { fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.25 },
    h2: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.3 },
    h3: { fontSize: '1.35rem', fontWeight: 600, lineHeight: 1.3 },
    h4: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.35 },
    h5: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.35 },
    h6: { fontSize: '1.0625rem', fontWeight: 600, lineHeight: 1.35 },
    body1: { fontSize: APP_BASE_FONT_SIZE, fontWeight: 400, lineHeight: 1.5 },
    body2: { fontSize: APP_CAPTION_FONT_SIZE, fontWeight: 400, lineHeight: 1.5 },
    subtitle1: { fontSize: APP_BASE_FONT_SIZE, fontWeight: 500, lineHeight: 1.5 },
    subtitle2: { fontSize: APP_CAPTION_FONT_SIZE, fontWeight: 500, lineHeight: 1.5 },
    caption: { fontSize: APP_CAPTION_FONT_SIZE, fontWeight: 400, lineHeight: 1.45 },
    button: { textTransform: 'none', fontWeight: 600, fontSize: APP_BASE_FONT_SIZE, lineHeight: 1.4 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          fontSize: '100%',
        },
        body: {
          fontSize: `${APP_BASE_FONT_SIZE} !important`,
          fontWeight: '400 !important',
          fontFamily: `${SIDEBAR_TEXT_FONT_FAMILY} !important`,
          lineHeight: '1.5 !important',
        },
        'input::placeholder, textarea::placeholder, .MuiInputBase-input::placeholder': {
          color: 'rgba(100, 116, 139, 0.72) !important',
          opacity: '1 !important',
        },
        '.MuiButton-root, .MuiInputBase-input, .MuiInputLabel-root, .MuiFormHelperText-root, .MuiMenuItem-root, .MuiTab-root, .MuiTableCell-root, .MuiChip-label, .MuiListItemText-primary, .MuiListItemText-secondary, .MuiAlert-message, .MuiFormLabel-root, .MuiFormControlLabel-label, .MuiAutocomplete-option, .MuiAutocomplete-option .MuiTypography-body1, .MuiAutocomplete-option .MuiTypography-body2, .MuiMenuItem-root .MuiTypography-body1, .MuiMenuItem-root .MuiTypography-body2, .MuiPopover-paper .MuiTypography-body1, .MuiPopover-paper .MuiTypography-body2, .MuiDialog-paper .MuiTypography-body1, .MuiDialog-paper .MuiTypography-body2, .MuiDialogTitle-root .MuiTypography-root, .MuiDialogContent-root .MuiTypography-body1, .MuiDialogContent-root .MuiTypography-body2, .MuiDialogActions-root .MuiTypography-root': {
          fontFamily: `${SIDEBAR_TEXT_FONT_FAMILY} !important`,
        },
        'input, textarea, select, option, button, label, small, p': {
          fontSize: `${APP_BASE_FONT_SIZE} !important`,
          fontFamily: `${SIDEBAR_TEXT_FONT_FAMILY} !important`,
          lineHeight: '1.5 !important',
        },
        'input::placeholder, textarea::placeholder': {
          fontSize: `${APP_BASE_FONT_SIZE} !important`,
          fontFamily: `${SIDEBAR_TEXT_FONT_FAMILY} !important`,
          lineHeight: '1.5 !important',
          opacity: '1 !important',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: SIDEBAR_TEXT_FONT_FAMILY,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 3px 8px rgba(32, 48, 88, 0.03)',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true, size: 'medium' },
      styleOverrides: {
        root: {
          borderRadius: 8,
          minHeight: 40,
          padding: '8px 16px',
          fontSize: APP_BASE_FONT_SIZE,
          fontWeight: 600,
        },
        sizeSmall: {
          minHeight: 34,
          padding: '6px 12px',
          fontSize: APP_CAPTION_FONT_SIZE,
        },
        sizeLarge: {
          minHeight: 46,
          padding: '10px 22px',
          fontSize: '1.0625rem',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': {
            minHeight: 40,
            borderRadius: 8,
            fontSize: `${APP_BASE_FONT_SIZE} !important`,
            fontWeight: 400,
            fontFamily: SIDEBAR_TEXT_FONT_FAMILY,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          fontSize: `${APP_BASE_FONT_SIZE} !important`,
          fontWeight: '400 !important' as any,
          fontFamily: `${SIDEBAR_TEXT_FONT_FAMILY} !important`,
          lineHeight: '1.5 !important',
        },
        root: {
          fontSize: `${APP_BASE_FONT_SIZE} !important`,
          fontWeight: '400 !important' as any,
          fontFamily: `${SIDEBAR_TEXT_FONT_FAMILY} !important`,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': {
            minHeight: 40,
            fontSize: `${APP_BASE_FONT_SIZE} !important`,
            fontWeight: 400,
            fontFamily: SIDEBAR_TEXT_FONT_FAMILY,
          },
          '& .MuiInputLabel-root': {
            fontSize: APP_BASE_FONT_SIZE,
            fontWeight: 500,
            fontFamily: SIDEBAR_TEXT_FONT_FAMILY,
          },
          '& .MuiFormHelperText-root': {
            fontSize: APP_CAPTION_FONT_SIZE,
            fontWeight: 400,
            fontFamily: SIDEBAR_TEXT_FONT_FAMILY,
            lineHeight: 1.45,
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        select: {
          minHeight: '40px !important',
          display: 'flex',
          alignItems: 'center',
          fontSize: `${APP_BASE_FONT_SIZE} !important`,
          fontWeight: '500 !important' as any,
          fontFamily: `${SIDEBAR_TEXT_FONT_FAMILY} !important`,
        },
        icon: {
          color: '#64748b',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: APP_BASE_FONT_SIZE,
          fontWeight: 500,
          fontFamily: SIDEBAR_TEXT_FONT_FAMILY,
          lineHeight: 1.45,
          minHeight: 44,
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        size: 'small',
        componentsProps: {
          popper: {
            modifiers: [popperSameWidthModifier as any],
          },
        },
      },
      styleOverrides: {
        inputRoot: {
          minHeight: 40,
          fontSize: APP_BASE_FONT_SIZE,
          fontWeight: 400,
          fontFamily: SIDEBAR_TEXT_FONT_FAMILY,
        },
        option: {
          fontSize: APP_BASE_FONT_SIZE,
          fontWeight: 400,
          fontFamily: SIDEBAR_TEXT_FONT_FAMILY,
          lineHeight: 1.45,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        // Hide MUI's JS-measured indicator everywhere. We render a pure CSS
        // underline via `::after` on the selected Tab (see MuiTab override
        // below). This avoids the well-known misalignment that happens when
        // the Inter web font / `!important` CssBaseline overrides re-flow
        // each Tab after MUI has already cached the indicator's left/width.
        indicator: {
          display: 'none',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 44,
          fontSize: APP_BASE_FONT_SIZE,
          textTransform: 'none',
          fontWeight: 500,
          fontFamily: SIDEBAR_TEXT_FONT_FAMILY,
          position: 'relative',
          '&.Mui-selected::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: 2,
            backgroundColor: '#1d4ed8',
            pointerEvents: 'none',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: APP_BASE_FONT_SIZE,
          fontWeight: 400,
          fontFamily: SIDEBAR_TEXT_FONT_FAMILY,
          lineHeight: 1.45,
        },
        head: {
          fontWeight: 600,
          fontSize: APP_BASE_FONT_SIZE,
          fontFamily: SIDEBAR_TEXT_FONT_FAMILY,
        },
      },
    },
    MuiTablePagination: {
      defaultProps: {
        SelectProps: {
          MenuProps: {
            PaperProps: { sx: { minWidth: 72 } },
          },
        },
      },
      styleOverrides: {
        select: {
          // Keep space between the rows-per-page value and the dropdown arrow.
          paddingRight: '28px !important',
        },
        selectIcon: {
          right: 4,
        },
      },
    },
  },
});
theme = responsiveFontSizes(theme);

/**
 * Keep dropdown popups exactly the same width as their trigger fields.
 * Applies to MUI Select menus and Autocomplete poppers.
 */
function useSyncMuiDropdownPopupWidth() {
  useLayoutEffect(() => {
    let raf = 0;
    const isFieldDropdownTrigger = (trigger: HTMLElement): boolean => {
      if (!trigger) return false;
      if (trigger.matches('[role="combobox"]')) return true;
      if (trigger.classList.contains('MuiSelect-select')) return true;
      if (trigger.closest('.MuiSelect-select')) return true;
      if (trigger.closest('.MuiAutocomplete-root')) return true;
      if (trigger.closest('.MuiFormControl-root')) return true;
      return false;
    };
    const getSelectTriggerWidth = (menuPaper: HTMLElement): number | null => {
      const listbox = menuPaper.querySelector('[role="listbox"]') as HTMLElement | null;
      const listboxId = listbox?.id ? String(listbox.id) : '';
      if (!listboxId) return null;
      const safeId = listboxId.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      const trigger = document.querySelector(`[aria-controls="${safeId}"]`) as HTMLElement | null;
      if (!trigger) return null;
      // TablePagination rows-per-page uses a tiny Select; matching its width crushes the menu (10/25/50/100).
      if (trigger.closest('.MuiTablePagination-root')) return null;
      if (!isFieldDropdownTrigger(trigger)) return null;
      // Collections / AI Agent selects: forcing paper width breaks alignment under tenant layout CSS zoom.
      if (trigger.id === 'project-select' || trigger.closest('#project-select')) return null;
      if (trigger.id === 'env-project-select' || trigger.closest('#env-project-select')) return null;
      if (trigger.id === 'auth-type-select' || trigger.closest('#auth-type-select')) return null;
      if (trigger.id === 'execute-type-select' || trigger.closest('#execute-type-select')) return null;
      if (trigger.id === 'round-robin-failure-select' || trigger.closest('#round-robin-failure-select')) return null;
      if (trigger.id === 'agent-intent-select' || trigger.closest('#agent-intent-select')) return null;
      if (typeof trigger.id === 'string' && trigger.id.startsWith('agent-')) return null;
      if (trigger.closest('.collection-request-env-select')) return null;
      const w = trigger.getBoundingClientRect().width;
      return Number.isFinite(w) && w > 0 ? w : null;
    };
    const sync = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        Array.from(document.querySelectorAll('.MuiPaper-root.MuiMenu-paper')).forEach((node) => {
          const el = node as HTMLElement;
          const triggerWidth = getSelectTriggerWidth(el);
          if (triggerWidth != null) {
            const w = `${Math.round(triggerWidth)}px`;
            el.style.width = w;
            el.style.minWidth = w;
            el.style.maxWidth = w;
          } else {
            // Do not force width for non-field menus (e.g., "..." action menus, TablePagination).
            el.style.removeProperty('width');
            el.style.removeProperty('min-width');
            el.style.removeProperty('max-width');
          }
        });

        Array.from(document.querySelectorAll('.MuiAutocomplete-popper')).forEach((node) => {
          const popper = node as HTMLElement;
          const popperW = popper.style.width;
          if (!popperW || !/^\d+(\.\d+)?px$/.test(popperW)) return;
          const paper = popper.querySelector('.MuiPaper-root') as HTMLElement | null;
          if (!paper) return;
          paper.style.width = popperW;
          paper.style.minWidth = popperW;
          paper.style.maxWidth = popperW;
          const listbox = popper.querySelector('.MuiAutocomplete-listbox') as HTMLElement | null;
          if (listbox) {
            listbox.style.width = '100%';
            listbox.style.minWidth = '100%';
            listbox.style.maxWidth = '100%';
            listbox.style.boxSizing = 'border-box';
          }
        });
      });
    };

    const observer = new MutationObserver((records) => {
      const touched = records.some((record) => {
        for (const n of Array.from(record.addedNodes)) {
          if (!(n instanceof Element)) continue;
          if (
            n.classList.contains('MuiMenu-paper') ||
            n.querySelector('.MuiMenu-paper') ||
            n.classList.contains('MuiAutocomplete-popper') ||
            n.querySelector('.MuiAutocomplete-popper')
          ) return true;
        }
        return false;
      });
      if (touched) sync();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', sync);
    document.addEventListener('pointerdown', sync, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', sync);
      document.removeEventListener('pointerdown', sync, true);
      cancelAnimationFrame(raf);
    };
  }, []);
}

function App() {
  useSyncMuiDropdownPopupWidth();
  return (
    <ResizeObserverErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AuthProvider>
            <TenantProvider>
              <ProjectProvider>
                <TestCycleExecutionProvider>
                  <CollectionExecutionProvider>
                    <PopupProvider>
                    <Router>
                      {/* Main domain routes (marketing pages) */}
                      <MainDomainRoutes />

                      {/* Tenant subdomain routes (application) */}
                      <TenantDomainRoutes />
                    </Router>
                    </PopupProvider>
                  </CollectionExecutionProvider>
                </TestCycleExecutionProvider>
              </ProjectProvider>
            </TenantProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ResizeObserverErrorBoundary>
  );
}

export default App; 